/** Robust statistics for reference-point consistency. All pure. */

import { OUTLIER_K } from '../../../config/consistencyScoring'

/** Median; NaN for empty input. */
export function median(values: number[]): number {
  if (values.length === 0) return NaN
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid]
}

/** Sample standard deviation (n−1 denominator); 0 for <2 values. */
function sampleStdDev(values: number[]): number {
  if (values.length < 2) return 0
  const mean = values.reduce((s, v) => s + v, 0) / values.length
  const ss = values.reduce((s, v) => s + (v - mean) * (v - mean), 0)
  return Math.sqrt(ss / (values.length - 1))
}

/** Index of the value farthest from the median; first max in input order on ties. */
function farthestFromMedianIndex(values: number[]): number {
  const med = median(values)
  let worst = 0
  for (let i = 1; i < values.length; i++) {
    if (Math.abs(values[i] - med) > Math.abs(values[worst] - med)) worst = i
  }
  return worst
}

/**
 * Leave-one-out trimmed spread: drop the single value farthest from the median,
 * then take the sample std-dev of the rest. One traffic lap cannot manufacture
 * a "LOOSE" — and the dropped lap is the one the outlier note names. With 2–3
 * values no trimming (an outlier can't be told apart from genuine spread); 0
 * for <2. Chosen over MAD, which collapses to 0 when ≥half the values tie
 * (chart speeds are integer-rounded, so ties are common) and, at N=3, reads
 * only the middle deviation.
 */
export function robustSpread(values: number[]): number {
  if (values.length < 2) return 0
  if (values.length < 4) return sampleStdDev(values)
  const worst = farthestFromMedianIndex(values)
  return sampleStdDev(values.filter((_, i) => i !== worst))
}

export interface OutlierResult {
  index: number
  value: number
  /** Signed distance from the median of the remaining values. */
  delta: number
  /** Spread of the values with the outlier removed. */
  typicalSpread: number
}

/**
 * Name the trimmed lap as an outlier only when it sits clearly outside the
 * rest: |value − median(rest)| must exceed BOTH K×spread(rest) and the
 * caller's absolute floor (the metric's okThreshold) — the floor stops
 * near-identical clusters naming a 1 km/h "outlier". Needs ≥4 values.
 */
export function detectSingleOutlier(
  values: number[],
  floor: number
): OutlierResult | null {
  if (values.length < 4) return null
  const worst = farthestFromMedianIndex(values)
  const rest = values.filter((_, i) => i !== worst)
  const restSpread = sampleStdDev(rest)
  const delta = values[worst] - median(rest)
  if (Math.abs(delta) <= Math.max(OUTLIER_K * restSpread, floor)) return null
  return {
    index: worst,
    value: values[worst],
    delta,
    typicalSpread: restSpread,
  }
}
